import { format } from 'date-fns'
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CirclePause,
  CircleX,
  ClipboardList,
  ExternalLink,
  Headset,
  Link2,
  Loader2,
  MessageSquareText,
  Paperclip,
  Pill
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { approvedStatuses, onHoldStatuses } from '@/lib/constants/med-requests'
import {
  cn,
  getMedRequestSubscriptionText,
  getOrderStatusBadgeClasses,
  isMedRequestSubscription,
  parseValidDate
} from '@/lib/utils'
import type { MedRequest } from '@/types/data'

const formatDay = (date: Date) => format(date, 'd MMM yyyy')

type Hold = NonNullable<MedRequest['onHoldHistory']>[number]

/** Newest first; legacy V1 rows only carry the flat reasonForHold. */
function getHoldsNewestFirst(order: MedRequest): Hold[] {
  if (order.onHoldHistory?.length) {
    return [...order.onHoldHistory].sort(
      (a, b) => (parseValidDate(b.date)?.getTime() ?? 0) - (parseValidDate(a.date)?.getTime() ?? 0)
    )
  }

  return order.reasonForHold
    ? [{ reason: order.reasonForHold, patientResponse: order.patientsResponseForHold ?? '', date: '' }]
    : []
}

type ActionStamp = {
  label: string
  date: Date
  tone: 'approved' | 'rejected' | 'onHold'
}

function getActionStamp(order: MedRequest, holds: Hold[], orderedAt: Date | null): ActionStamp | null {
  const actionedAt = parseValidDate(order.actionedAt)
  // Renewals inherit the original order's action stamp, which then predates the renewal itself.
  const ownActionedAt = actionedAt && !(orderedAt && actionedAt < orderedAt) ? actionedAt : null

  if (onHoldStatuses.includes(order.stage)) {
    const date = parseValidDate(holds[0]?.date) ?? ownActionedAt
    return date ? { label: 'On hold', date, tone: 'onHold' } : null
  }

  // Only prescriber actions move actionedAt, so a refund (always automated) would show an earlier action's date.
  if (!ownActionedAt) return null
  if (approvedStatuses.includes(order.stage)) return { label: 'Approved', date: ownActionedAt, tone: 'approved' }
  return order.stage === 'Rejected' ? { label: 'Rejected', date: ownActionedAt, tone: 'rejected' } : null
}

const stampStyles = {
  approved: { className: 'text-[#388E10]', Icon: CircleCheck },
  rejected: { className: 'text-[#FF4D1A]', Icon: CircleX },
  onHold: { className: 'text-[#CC8800]', Icon: CirclePause }
}

function ActionStampLabel({ stamp }: { stamp: ActionStamp }) {
  const { className, Icon } = stampStyles[stamp.tone]

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <Icon aria-hidden className='size-4 shrink-0' />
      {stamp.label} {formatDay(stamp.date)}
    </span>
  )
}

const pillClassName =
  'inline-flex items-center gap-1.5 rounded-full border border-custom-neutral-700 bg-white px-3 py-1 text-[13px] leading-5 text-custom-neutral-800'

const pillButtonClassName = cn(pillClassName, 'cursor-pointer transition-colors hover:border-custom-neutral-400')

function NoteRow({
  icon,
  iconClassName,
  label,
  text,
  details
}: {
  icon: ReactNode
  iconClassName: string
  label: string
  text: string
  details?: ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <button
      type='button'
      aria-expanded={isOpen}
      onClick={() => setIsOpen(open => !open)}
      className='flex w-full items-start gap-3 border-t border-custom-neutral-200 px-6 py-3 text-left text-[13px] leading-5 cursor-pointer transition-colors hover:bg-white/60'
    >
      <span className={cn('grid size-7 shrink-0 place-items-center rounded-lg', iconClassName)}>{icon}</span>
      <span className='flex min-w-0 flex-1 flex-col gap-2 py-1'>
        <span className='flex min-w-0 gap-1.5'>
          <span className='shrink-0 font-semibold text-[#1D1D1D]'>{label}:</span>
          <span
            className={cn(
              'min-w-0 text-custom-neutral-600',
              isOpen ? 'whitespace-pre-wrap wrap-break-word' : 'truncate'
            )}
          >
            {text}
          </span>
        </span>
        {isOpen && details}
      </span>
      <span className='grid size-7 shrink-0 place-items-center rounded-full border border-custom-neutral-700 bg-white'>
        <ChevronDown
          aria-hidden
          className={cn('size-4 text-custom-neutral-800 transition-transform', isOpen && 'rotate-180')}
        />
      </span>
    </button>
  )
}

function OrderNotes({
  order,
  holds,
  changedDirections
}: {
  order: MedRequest
  holds: Hold[]
  changedDirections: string | null
}) {
  // NodeAPI maps a refunded row's justification here too, which is as often an approval reason as a rejection.
  const isRejected = order.stage === 'Rejected'

  return (
    <>
      {order.additionalNotes?.pharmacist && (
        <NoteRow
          icon={<ClipboardList aria-hidden className='size-4' />}
          iconClassName='bg-[#EEEAFB] text-[#5B45C2]'
          label='Pharmacy note'
          text={order.additionalNotes.pharmacist}
        />
      )}
      {order.additionalNotes?.customerService && (
        <NoteRow
          icon={<Headset aria-hidden className='size-4' />}
          iconClassName='bg-[#E7ECFA] text-[#153CB7]'
          label='Customer service note'
          text={order.additionalNotes.customerService}
        />
      )}
      {holds.map((hold, index) => {
        const heldAt = parseValidDate(hold.date)
        return (
          <NoteRow
            key={index}
            icon={<CirclePause aria-hidden className='size-4' />}
            iconClassName='bg-[#FFF1D6] text-[#CC8800]'
            label='On hold reason'
            text={hold.reason || '-'}
            details={
              <span className='flex flex-col gap-1 text-custom-neutral-600'>
                <span className='whitespace-pre-wrap wrap-break-word'>
                  <span className='font-semibold text-[#1D1D1D]'>Patient response: </span>
                  {hold.patientResponse || 'No response recorded'}
                </span>
                {heldAt && <span className='text-xs'>Placed on hold {format(heldAt, 'd MMM yyyy, HH:mm')}</span>}
              </span>
            }
          />
        )
      })}
      {order.reasonForRejection && (
        <NoteRow
          icon={
            isRejected ? (
              <CircleX aria-hidden className='size-4' />
            ) : (
              <MessageSquareText aria-hidden className='size-4' />
            )
          }
          iconClassName={
            isRejected ? 'rounded-full bg-[#FF4D1A] text-white' : 'bg-custom-neutral-200 text-custom-neutral-800'
          }
          label={isRejected ? 'Rejection reason' : 'Prescriber reason'}
          text={order.reasonForRejection}
        />
      )}
      {changedDirections && (
        <NoteRow
          icon={<Pill aria-hidden className='size-4' />}
          iconClassName='bg-custom-neutral-200 text-custom-neutral-800'
          label='Changed directions'
          text={changedDirections}
        />
      )}
    </>
  )
}

type PreviousMedicationCardProps = {
  order: MedRequest
  orderedAt: Date | null
  medicationName: string
  changedDirections: string | null
  categoryPath: string[]
  imageCount: number
  workDriveLinks: string[]
  isOpeningImages: boolean
  linkedLabel: string | null
  onOpenImages: () => void
}

export function PreviousMedicationCard({
  order,
  orderedAt,
  medicationName,
  changedDirections,
  categoryPath,
  imageCount,
  workDriveLinks,
  isOpeningImages,
  linkedLabel,
  onOpenImages
}: PreviousMedicationCardProps) {
  const holds = getHoldsNewestFirst(order)
  const stamp = getActionStamp(order, holds, orderedAt)

  return (
    <article
      data-testid='previous-medication-card'
      className='shrink-0 overflow-hidden rounded-2xl border border-custom-neutral-200 bg-custom-gray-3'
    >
      <Link
        to={`/request/${order.id}`}
        target='_blank'
        rel='noopener noreferrer'
        title='Open this order in a new tab'
        className='m-2 flex items-start justify-between gap-4 rounded-xl border border-transparent bg-white px-4 py-3 transition-colors hover:border-[#153CB7]'
      >
        <div className='min-w-0'>
          {categoryPath.length > 0 && (
            <p className='flex min-w-0 items-center gap-1 text-[13px] leading-5 text-custom-neutral-500'>
              {categoryPath.map((segment, index) => (
                <span key={index} className='flex min-w-0 items-center gap-1'>
                  {index > 0 && <ChevronRight aria-hidden className='size-3 shrink-0' />}
                  <span className='truncate'>{segment}</span>
                </span>
              ))}
            </p>
          )}
          <p className='truncate text-base font-medium leading-6 text-[#1D1D1D]'>{medicationName}</p>
        </div>
        <span
          className={cn(
            'shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium leading-4',
            getOrderStatusBadgeClasses(order.stage)
          )}
        >
          {order.stage}
        </span>
      </Link>

      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-6 pt-1 pb-3'>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] leading-5'>
          <span className='inline-flex items-center gap-1.5 text-custom-neutral-600'>
            <CalendarDays aria-hidden className='size-4 shrink-0' />
            Ordered {orderedAt ? formatDay(orderedAt) : 'on an unknown date'}
          </span>
          {stamp && (
            <>
              <span aria-hidden className='h-4 w-px bg-custom-neutral-700' />
              <ActionStampLabel stamp={stamp} />
            </>
          )}
        </div>

        <div className='flex flex-wrap items-center gap-2'>
          {imageCount > 0 && (
            <button type='button' onClick={onOpenImages} disabled={isOpeningImages} className={pillButtonClassName}>
              {isOpeningImages ? (
                <Loader2 aria-hidden className='size-4 animate-spin' />
              ) : (
                <Paperclip aria-hidden className='size-4' />
              )}
              {imageCount} {imageCount === 1 ? 'image' : 'images'} attached
            </button>
          )}
          {workDriveLinks.map((link, index) => (
            <a
              key={index}
              href={link}
              target='_blank'
              rel='noopener noreferrer'
              title='Opens the password-protected upload in Zoho WorkDrive'
              className={pillButtonClassName}
            >
              <ExternalLink aria-hidden className='size-4' />
              View upload{workDriveLinks.length > 1 && ` ${index + 1}`}
            </a>
          ))}
          {isMedRequestSubscription(order.subscriptionFrequency) && (
            <span className={pillClassName}>
              Subscription ({getMedRequestSubscriptionText(order.subscriptionFrequency)})
            </span>
          )}
          {order.isReorder && <span className={pillClassName}>Re-order</span>}
          {linkedLabel && (
            <span className={cn(pillClassName, 'border-[#153CB7] text-[#153CB7]')}>
              <Link2 aria-hidden className='size-4' />
              Linked patient order: {linkedLabel}
            </span>
          )}
        </div>
      </div>

      <OrderNotes order={order} holds={holds} changedDirections={changedDirections} />
    </article>
  )
}
